/**
 * @file 解いた結果の見せ方と絞り込み (純関数)。
 *
 * @what: ○△× の表示名、絞り込み (まだ解いていない / 解けなかった / あやしい / 解けた) の
 *   選択肢と判定、件数。
 * @why: リーダーの問題ごとの面で「次に何を解くか」を選ぶための軸。数は絞り込みの案内で、
 *   達成率は出さない。
 * @failure: 純関数。結果の無い問は「まだ解いていない」。
 */

import type { StudyItemResultKind, StudyItemResults } from "../api/itemResults";

export const ITEM_RESULT_LABELS: Record<StudyItemResultKind, string> = {
  ok: "解けた",
  partial: "あやしい",
  ng: "解けなかった",
};

export const ITEM_RESULT_MARKS: Record<StudyItemResultKind, string> = {
  ok: "○",
  partial: "△",
  ng: "×",
};

/** 絞り込みの値 ("" = すべて)。 */
export type ItemResultFilter = "" | "none" | "ng" | "partial" | "ok";

export const ITEM_RESULT_FILTERS: { value: ItemResultFilter; label: string }[] = [
  { value: "", label: "すべて" },
  { value: "none", label: "まだ解いていない" },
  { value: "ng", label: "解けなかった" },
  { value: "partial", label: "あやしい" },
  { value: "ok", label: "解けた" },
];

export function isItemResultFilter(v: string | null): v is ItemResultFilter {
  return v === "" || v === "none" || v === "ng" || v === "partial" || v === "ok";
}

/** 問が絞り込みに合うか (直近の結果で見る)。 */
export function matchesResultFilter(
  results: StudyItemResults,
  itemId: string,
  filter: ItemResultFilter,
): boolean {
  if (filter === "") return true;
  const last = results[itemId]?.last.result;
  if (filter === "none") return last === undefined;
  return last === filter;
}

/** 直近の結果ごとの件数 (絞り込みの案内用)。 */
export function countByResult(
  results: StudyItemResults,
  itemIds: readonly string[],
): Record<ItemResultFilter, number> {
  const c: Record<ItemResultFilter, number> = {
    "": itemIds.length,
    none: 0,
    ng: 0,
    partial: 0,
    ok: 0,
  };
  for (const id of itemIds) {
    const last = results[id]?.last.result;
    if (last === undefined) c.none++;
    else c[last]++;
  }
  return c;
}
